
import React from "react";
import { VideoBackground } from "./VideoBackground";
import { useVideoUrlChecker, createBackgroundStyle } from "../utils/templateUtils";
import type { ThemeCustomizations } from "../types";

interface ModernTemplateBackgroundProps {
  themeCustomizations: ThemeCustomizations;
  children: React.ReactNode;
}

export const ModernTemplateBackground: React.FC<ModernTemplateBackgroundProps> = React.memo(({
  themeCustomizations,
  children
}) => {
  const isVideoUrl = useVideoUrlChecker();

  // Process backgroundColor to handle both string and object formats
  const processedBackgroundColor = React.useMemo(() => {
    const bgColor = themeCustomizations.backgroundColor;
    if (!bgColor) return undefined;
    
    // Handle object format from form data
    if (typeof bgColor === 'object' && bgColor !== null && 'value' in (bgColor as object)) {
      return (bgColor as { value: string }).value;
    }
    
    // Handle string format
    if (typeof bgColor === 'string') {
      return bgColor;
    }
    
    return undefined;
  }, [themeCustomizations.backgroundColor]);

  const templateStyle = React.useMemo(() => createBackgroundStyle(
    themeCustomizations.backgroundImageUrl,
    processedBackgroundColor,
    isVideoUrl
  ), [themeCustomizations.backgroundImageUrl, processedBackgroundColor, isVideoUrl]);

  const hasCustomBackground = React.useMemo(() => 
    !!(themeCustomizations.backgroundImageUrl || processedBackgroundColor), 
    [themeCustomizations.backgroundImageUrl, processedBackgroundColor]
  );

  const hasVideoBackground = React.useMemo(() => 
    !!(themeCustomizations.backgroundImageUrl && isVideoUrl(themeCustomizations.backgroundImageUrl)),
    [themeCustomizations.backgroundImageUrl, isVideoUrl]
  );

  return (
    <div 
      className={`h-screen ${hasCustomBackground ? '' : 'bg-gradient-to-br from-slate-50 to-blue-50'} ${hasVideoBackground ? 'relative overflow-hidden' : ''}`}
      style={templateStyle}
    >
      {hasVideoBackground && (
        <VideoBackground videoUrl={themeCustomizations.backgroundImageUrl!} />
      )}
      
      <div className={`${hasVideoBackground ? 'relative z-10' : ''}`}>
        {children}
      </div>
    </div>
  );
});

ModernTemplateBackground.displayName = 'ModernTemplateBackground';
